import {Icon} from '../Icon';
import {cn} from '@/utils/common_functions';
import {Text} from '../Text';
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  FocusEvent,
} from 'react';
import {InputStateTypes} from '@/interface';

interface DateInputInterface {
  label?: string;
  value: Date | null;
  name?: string;
  onChangeDate: (value: Date | null) => void;
  disabled?: boolean;
  error?: string;
  touched?: boolean;
  placeholder?: string;
  required?: boolean;
  className?: string;
  iconClassName?: string;
  wrapperClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  textClassName?: string;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  maxValue?: Date;
  minValue?: Date;
}
export const DateInput: React.FC<DateInputInterface> = props => {
  const {
    value,
    className,
    disabled,
    error = '',
    iconClassName,
    wrapperClassName,
    inputClassName,
    textClassName,
    label,
    labelClassName,
    name,
    onChangeDate,
    placeholder,
    required,
    touched,
    onBlur,
    onFocus,
    maxValue,
    minValue,
  } = props;

  const [inputState, setInputState] = useState<InputStateTypes>('default');
  const datePickerRef = useRef<HTMLInputElement | null>(null);
  /**
   * a utily function to update input state with a check to disabled logic,
   */
  const updateInputState = useCallback(
    (state: InputStateTypes) => {
      if (disabled) {
        return;
      }
      setInputState(state);
    },
    [disabled],
  );

  /**
   * utility wrapper for onBlur handler, such that if also updates the input state locally
   */
  function onBlurHandler(e: FocusEvent<HTMLInputElement>): void {
    updateInputState(error && touched ? 'error' : 'default');
    onBlur?.(e);
  }

  /**
   * utility wrapper for onFocus handler, such that if also updates the input state locally
   */
  function onFocusHandler(e: FocusEvent<HTMLInputElement>): void {
    updateInputState('focus');
    onFocus?.(e);
  }

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const dateString = e.target.value;
    if (dateString) {
      onChangeDate(new Date(dateString));
    } else {
      onChangeDate(null);
    }
  };

  /**
   * handle open file picker
   */

  const handleOpenDatePicker = () => {
    if (datePickerRef.current) {
      datePickerRef.current.focus();
      datePickerRef.current.showPicker();
    }
  };

  const formatDateForInput = (date: Date | null): string => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateForPlaceholder = (date: Date | null): string => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${day}-${month}-${year}`;
  };

  useEffect(() => {
    if (touched && error) {
      updateInputState('error');
    } else {
      updateInputState('default');
    }
  }, [error, touched]);

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {label && (
        <Text className={cn('!font-FigtreeSemiBold inline-flex gap-1 text-ui_blue', labelClassName)} variant="body">
          {label}
          {required && <p className="text-rejection">*</p>}
        </Text>
      )}
      <div
        className={cn(
          'border-2 flex border-ui_input_border relative text-base rounded-lg items-center disabled:cursor-not-allowed w-full',
          inputState === 'error' && 'border-rejection',
          inputState === 'focus' && 'border-black',
          inputState === 'disabled' && 'border-x-ui_disabled',
          wrapperClassName,
        )}>
        <Text
          className={cn(
            'px-4 py-2 w-full rounded-lg self-stretch outline-none flex-1 min-w-0 bg-transparent whitespace-nowrap',
            !value && 'text-ui_input_border_dark',
            textClassName
          )}>
          {value ? formatDateForPlaceholder(value) : placeholder}
        </Text>

        <input
          ref={datePickerRef}
          type="date"
          name={name}
          disabled={disabled}
          value={formatDateForInput(value)}
          onChange={handleDateChange}
          onFocus={onFocusHandler}
          onBlur={onBlurHandler}
          placeholder={placeholder}
          className={cn('size-0', inputClassName)}
          max={maxValue ? formatDateForInput(maxValue) : undefined}
          min={minValue ? formatDateForInput(minValue) : undefined}
        />

        <button
          type="button"
          onMouseDown={e => e.preventDefault()} // Prevents focus shift
          disabled={disabled}
          onClick={handleOpenDatePicker}>
          <Icon
            type="calender"
            className={cn('size-4 mr-4 cursor-pointer', iconClassName)}
          />
        </button>
      </div>

      {touched && error && (
        <Text className="text-rejection text-sm">{error}</Text>
      )}
    </div>
  );
};
